/**
 * Score Breakdown Bars Component
 *
 * Displays horizontal progress bars for consistency, recency, and volatility scores
 * with color coding based on score ranges:
 * - Green (good): 70-100
 * - Yellow (medium): 40-69
 * - Red (poor): 0-39
 *
 * Each bar shows the score label, numeric value, and a filled progress bar.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  Activity,
  Clock,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";

// ============================================================================
// Types
// ============================================================================

/**
 * Score type identifiers
 */
export type ScoreType = "consistency" | "recency" | "volatility";

/**
 * Score range classification
 */
export type ScoreRange = "good" | "medium" | "poor";

/**
 * Individual score data
 */
export interface ScoreData {
  /** Score type */
  type: ScoreType;
  /** Score value (0-100) */
  value: number;
  /** Optional custom label override */
  label?: string;
}

/**
 * Props for the ScoreBreakdownBars component
 */
export interface ScoreBreakdownBarsProps {
  /** Consistency score (0-100) */
  consistencyScore: number;
  /** Recency score (0-100) */
  recencyScore: number;
  /** Volatility score (0-100) */
  volatilityScore: number;
  /** Optional title override */
  title?: string;
  /** Whether to show the card wrapper */
  showCard?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Score range thresholds
 */
const SCORE_THRESHOLDS = {
  good: 70,
  medium: 40,
} as const;

/**
 * Score configuration with colors, labels, and icons
 */
const SCORE_CONFIG: Record<
  ScoreType,
  {
    label: string;
    description: string;
    icon: LucideIcon;
  }
> = {
  consistency: {
    label: "Consistency",
    description: "Performance stability over time",
    icon: TrendingUp,
  },
  recency: {
    label: "Recency",
    description: "Recent performance strength",
    icon: Clock,
  },
  volatility: {
    label: "Volatility",
    description: "Price fluctuation management",
    icon: Activity,
  },
};

/**
 * Color configuration based on score range
 */
const RANGE_COLORS: Record<
  ScoreRange,
  {
    bgColor: string;
    textColor: string;
    trackColor: string;
    label: string;
  }
> = {
  good: {
    bgColor: "bg-emerald-500",
    textColor: "text-emerald-700 dark:text-emerald-400",
    trackColor: "bg-emerald-100 dark:bg-emerald-900/30",
    label: "Good",
  },
  medium: {
    bgColor: "bg-amber-500",
    textColor: "text-amber-700 dark:text-amber-400",
    trackColor: "bg-amber-100 dark:bg-amber-900/30",
    label: "Average",
  },
  poor: {
    bgColor: "bg-rose-500",
    textColor: "text-rose-700 dark:text-rose-400",
    trackColor: "bg-rose-100 dark:bg-rose-900/30",
    label: "Poor",
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the score range classification based on value
 */
function getScoreRange(score: number): ScoreRange {
  if (score >= SCORE_THRESHOLDS.good) return "good";
  if (score >= SCORE_THRESHOLDS.medium) return "medium";
  return "poor";
}

/**
 * Clamp score to valid range (0-100)
 */
function clampScore(score: number): number {
  return Math.max(0, Math.min(100, score));
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Individual score bar with label, value, and progress indicator
 */
function ScoreBar({
  type,
  value,
  customLabel,
}: {
  type: ScoreType;
  value: number;
  customLabel?: string;
}) {
  const config = SCORE_CONFIG[type];
  const clampedValue = clampScore(value);
  const range = getScoreRange(clampedValue);
  const rangeConfig = RANGE_COLORS[range];
  const Icon = config.icon;

  return (
    <div className="space-y-2">
      {/* Label Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">
            {customLabel ?? config.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn("text-sm font-semibold", rangeConfig.textColor)}>
            {clampedValue}
          </span>
          <span className="text-xs text-muted-foreground">/ 100</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            rangeConfig.bgColor,
            "h-full rounded-full transition-all duration-500 ease-out"
          )}
          style={{ width: `${clampedValue}%` }}
          role="progressbar"
          aria-valuenow={clampedValue}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${config.label}: ${clampedValue} out of 100`}
        />
      </div>

      {/* Description (optional, shown on hover or for accessibility) */}
      <p className="sr-only">{config.description}</p>
    </div>
  );
}

/**
 * Score legend showing the color coding
 */
function ScoreLegend() {
  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
      {(["good", "medium", "poor"] as const).map((range) => {
        const config = RANGE_COLORS[range];
        return (
          <div key={range} className="flex items-center gap-1.5">
            <div className={cn("h-2 w-2 rounded-full", config.bgColor)} />
            <span>
              {config.label} ({range === "good" ? "70-100" : range === "medium" ? "40-69" : "0-39"})
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Score Breakdown Bars
 *
 * Displays horizontal progress bars showing consistency, recency, and volatility
 * scores with color-coded indicators based on performance levels.
 *
 * @example
 * ```tsx
 * <ScoreBreakdownBars
 *   consistencyScore={85}
 *   recencyScore={72}
 *   volatilityScore={45}
 * />
 * ```
 *
 * @example Without card wrapper
 * ```tsx
 * <ScoreBreakdownBars
 *   consistencyScore={85}
 *   recencyScore={72}
 *   volatilityScore={45}
 *   showCard={false}
 * />
 * ```
 */
export function ScoreBreakdownBars({
  consistencyScore,
  recencyScore,
  volatilityScore,
  title = "Score Breakdown",
  showCard = true,
  className,
}: ScoreBreakdownBarsProps) {
  const content = (
    <div className="space-y-4">
      {/* Consistency Score */}
      <ScoreBar type="consistency" value={consistencyScore} />

      {/* Recency Score */}
      <ScoreBar type="recency" value={recencyScore} />

      {/* Volatility Score */}
      <ScoreBar type="volatility" value={volatilityScore} />

      {/* Legend */}
      <div className="pt-2 border-t border-border/50">
        <ScoreLegend />
      </div>
    </div>
  );

  if (!showCard) {
    return <div className={cn(className)}>{content}</div>;
  }

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for ScoreBreakdownBars
 */
export function ScoreBreakdownBarsSkeleton({
  showCard = true,
  className,
}: {
  showCard?: boolean;
  className?: string;
}) {
  const content = (
    <div className="space-y-4">
      {/* Score Bar Skeletons */}
      {[...Array(3)].map((_, index) => (
        <div key={index} className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-8" />
              <Skeleton className="h-3 w-8" />
            </div>
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
        </div>
      ))}

      {/* Legend Skeleton */}
      <div className="pt-2 border-t border-border/50">
        <div className="flex flex-wrap items-center gap-4">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="flex items-center gap-1.5">
              <Skeleton className="h-2 w-2 rounded-full" />
              <Skeleton className="h-3 w-16" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (!showCard) {
    return <div className={cn(className)}>{content}</div>;
  }

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-3">
        <Skeleton className="h-5 w-32" />
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}

export default ScoreBreakdownBars;
